import * as Stimulus from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

const ZIP_CODE_PATTERN = /^\d{5}(?:-\d{4})?$/

export class ZipCodeCityController extends Stimulus.Controller {
    static targets = ["zipInput", "cityInput", "button"]

    lookupDebounceTimer = null
    previousZipCode = null
    lookupRequestId = 0

    connect() {
        if (!this.hasZipInputTarget || !this.hasButtonTarget) {
            return
        }

        const zipCode = `${this.zipInputTarget.value || ""}`.trim()
        if (zipCode.length > 0) {
            this.lookup()
        }
    }

    disconnect() {
        clearTimeout(this.lookupDebounceTimer)
    }

    lookupDebounce() {
        this.#clearZipCodeValidity()
        clearTimeout(this.lookupDebounceTimer)
        this.lookupDebounceTimer = setTimeout(() => {
            this.lookup()
            this.lookupDebounceTimer = null
        }, 300)
    }

    async lookup() {
        if (!this.hasZipInputTarget || !this.hasButtonTarget) {
            return
        }

        const zipCode = `${this.zipInputTarget.value || ""}`.trim()
        if (zipCode === this.previousZipCode) {
            return
        }
        this.previousZipCode = zipCode

        if (zipCode.length === 0 || !ZIP_CODE_PATTERN.test(zipCode)) {
            this.#clearZipCodeValidity()
            this.#clearCityInput()
            return
        }

        const form = this.buttonTarget.form
        if (!form) {
            return
        }

        const requestId = ++this.lookupRequestId
        try {
            const response = await fetch(this.buttonTarget.formAction || form.action, {
                method: "POST",
                headers: {
                    Accept: "text/vnd.turbo-stream.html",
                },
                body: new FormData(form),
                credentials: "same-origin",
            })

            if (requestId !== this.lookupRequestId) {
                return
            }

            if (!response.ok) {
                this.#clearCityInput()
                this.#setZipCodeLookupError()
                return
            }

            this.#clearZipCodeValidity()
            const stream = await response.text()
            if (stream.length > 0) {
                Turbo.renderStreamMessage(stream)
            }
        } catch (_) {
            if (requestId !== this.lookupRequestId) {
                return
            }

            this.#clearCityInput()
        }
    }

    #clearCityInput() {
        if (!this.hasCityInputTarget) {
            return
        }

        this.cityInputTarget.value = ""
        this.cityInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    #clearZipCodeValidity() {
        if (!this.hasZipInputTarget) {
            return
        }

        this.zipInputTarget.setCustomValidity("")
    }

    #setZipCodeLookupError() {
        if (!this.hasZipInputTarget) {
            return
        }

        this.zipInputTarget.setCustomValidity("Please enter a valid zip code.")
        // Route the error through the app's input/fieldset controllers instead of
        // triggering the browser's native validation popup.
        this.zipInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
}
